import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import SteppedConsultationForm, {
  ConsultationPath,
} from "@/components/forms/SteppedConsultationForm";
import SteppedConsultationFormV2, {
  V2Path,
} from "@/components/forms/SteppedConsultationFormV2";
import LabsBookingForm from "@/components/forms/LabsBookingForm";
import { intakeV2Enabled } from "@/lib/intakeV2Flag";

export type ConsultationVariant = "consultation" | "demo";
export type ConsultationIntent = "design" | "value_analysis" | "labs";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  variant?: ConsultationVariant;
  defaultIntent?: ConsultationIntent;
}

const ConsultationModal = ({
  open,
  onOpenChange,
  variant = "consultation",
  defaultIntent,
}: Props) => {
  const initialIntent: ConsultationIntent =
    defaultIntent ??
    (variant === "demo" ? "labs" : ("" as unknown as ConsultationIntent));
  const [intent, setIntent] = useState<ConsultationIntent | "">(initialIntent);
  const [resetKey, setResetKey] = useState(0);

  useEffect(() => {
    if (open) {
      setIntent(initialIntent);
      setResetKey((k) => k + 1);
    }
  }, [open, initialIntent]);

  const showLabs = intent === "labs";
  const startingPath: ConsultationPath | undefined =
    intent === "design" || intent === "value_analysis" ? intent : undefined;

  const title = showLabs
    ? "Request a codelumenLabs Presentation"
    : "Tell Us About Your Project";

  const description = showLabs
    ? "In person in El Paso or virtually. A senior team member will confirm within one business day."
    : "We'll recommend the right engagement path for your project. A senior team member will respond within one business day.";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-charcoal border-white/10 text-white
                   max-sm:top-0 max-sm:left-0 max-sm:translate-x-0 max-sm:translate-y-0
                   max-sm:h-[100dvh] max-sm:max-h-[100dvh] max-sm:w-screen max-sm:max-w-none max-sm:rounded-none
                   sm:max-w-2xl sm:max-h-[92dvh]
                   overflow-y-auto overscroll-contain"
      >
        <DialogHeader>
          <DialogTitle className="font-sans font-light text-xl md:text-2xl tracking-[-0.01em] text-white">
            {title}
          </DialogTitle>
          <DialogDescription className="text-white/60 font-sans font-light text-sm leading-relaxed">
            {description}
          </DialogDescription>
        </DialogHeader>

        <div className="mt-6">
          {showLabs ? (
            <LabsBookingForm />
          ) : intakeV2Enabled() ? (
            <SteppedConsultationFormV2
              key={`v2-${resetKey}`}
              resetKey={resetKey}
              defaultPath={startingPath as V2Path | undefined}
              onLabsClick={() => setIntent("labs")}
            />
          ) : (
            <SteppedConsultationForm
              key={resetKey}
              resetKey={resetKey}
              defaultPath={startingPath}
              onLabsClick={() => setIntent("labs")}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>

  );
};

export default ConsultationModal;
